import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, Activity, Truck, Languages, Ear, ShieldCheck, Users } from 'lucide-react';
const services = [{
  icon: Languages,
  name: 'Language Services',
  description: 'On-demand interpretation and document translation in multiple languages, both telephonic and on-site.'
}, {
  icon: Truck,
  name: 'Transportation Services',
  description: 'Reliable medical transportation to and from appointments, including specialty vehicles when needed.'
}, {
  icon: Ear,
  name: 'Hearing Aids',
  description: 'Hearing evaluations, hearing aids, and tinnitus assessments through a vetted national network.'
}, {
  icon: Activity,
  name: 'Home Health',
  description: 'Coordinated home health nursing and care services to support injured workers in their recovery environment.'
}];
const benefits = [{
  icon: Users,
  title: 'One coordinator',
  stat: '1:1',
  description: 'A single dedicated coordinator owns the case end-to-end — no handoffs, no lost details.'
}, {
  icon: ShieldCheck,
  title: 'Quality assurance',
  stat: 'Quality',
  description: 'Every provider in our network is credentialed, monitored, and outcomes-tracked.'
}];
export function AncillaryServices() {
  return <div className="bg-brand-paper" data-id="element-396" data-mp="1y4o3nk4x0x4b">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-397" data-mp="1t3gxtqmc280a">
        <div className="absolute inset-0 opacity-10" data-id="element-398" data-mp="1o4x9xko6im5a">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-399" data-mp="1o5oxc87mhdkh"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-400" data-mp="3ehcxupqr94xt"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-401" data-mp="1bu5wuvm9m8rk">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-402" data-mp="1gle9e0iw6a8y">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-403" data-mp="1ulfa9fz5phof">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-404" data-mp="1ziz0le94pkwq">
                Featured Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-405" data-mp="1qj2ftnpv166y">
              A+ <span className="text-brand-gold italic" data-id="element-406" data-mp="a4j1h60kpaaf">Ancillary</span>{' '}
              Services
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-407" data-mp="f5q831qzc3fi">
              A single, integrated solution for ancillary needs in your workers'
              compensation program — from language and transportation to hearing
              aids, home health, and beyond.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-408" data-mp="gjhrrsghmr08">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-409" data-mp="bnaoy8je1vmi">
                Schedule a Meeting With Us{' '}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-410" data-mp="3tsdsvpxmc1lc" />
              </Link>
              <a href="tel:7142591053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-411" data-mp="3p1nrensbcivx">
                <Phone className="w-5 h-5 mr-2" data-id="element-412" data-mp="2wdi5hxpiwnui" /> 714.259.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro / Why Ancillary */}
      <section className="py-24 bg-white" data-id="element-413" data-mp="24k6cyyixkog2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-414" data-mp="24kxz7iqn19to">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-415" data-mp="28kqcg8qz845d">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-416" data-mp="2sb20hjgcsxhi"></div>
            <div className="relative h-[560px] grid grid-rows-[1.3fr_1fr] gap-4" data-id="element-417" data-mp="2waawkuyv97g8">
              <figure className="relative overflow-hidden rounded-2xl shadow-2xl" data-id="element-418" data-mp="xwrfxn6skphj">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/1ec3ae75-be30-439d-9da2-2625f14d300b.jpg" alt="Accessible medical transportation arriving at an outpatient clinic" className="h-full w-full object-cover" data-id="element-419" data-mp="11xbe8u33pz59" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-brand-navy/85 px-5 py-4 text-white" data-id="element-420" data-mp="knqtwuchuqzt">
                  <span className="text-xs font-semibold tracking-widest text-brand-gold uppercase" data-id="element-421" data-mp="gnghozy3z4ci">
                    Transportation Services
                  </span>
                </figcaption>
              </figure>
              <figure className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-422" data-mp="18f4mn1ek4olb">
                <img src="https://cdn.magicpatterns.com/patterns/generated-images/59a028a6-f744-4539-98eb-49a924caa9ef.jpg" alt="Modern hearing aids beside a hearing assessment chart" className="h-full w-full object-cover" data-id="element-423" data-mp="14b6qmzkj3twh" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-brand-navy/85 px-5 py-4 text-white" data-id="element-424" data-mp="3k349o7hgv9fp">
                  <span className="text-xs font-semibold tracking-widest text-brand-gold uppercase" data-id="element-425" data-mp="3llkutsy52psn">
                    Hearing Aids
                  </span>
                </figcaption>
              </figure>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-426" data-mp="3cm7fijnc3700">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-427" data-mp="2swx3bfmsr46n">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-428" data-mp="2xsm6dsy0z37p">
              Fragmented ancillary care is costing you — and your injured
              workers.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-429" data-mp="2ow3f0l5fznd8">
              When language, transportation, hearing, and home health are
              coordinated across multiple vendors, claims slow down. Injured
              workers wait days for appointments. Adjusters chase paperwork.
              Costs creep up.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-430" data-mp="2a7brg4tllr4r">
              A+ Ancillary Services consolidates ancillary needs under one roof,
              one coordinator, and one transparent reporting dashboard — so care
              moves at the speed of recovery, not paperwork.
            </p>
            <ul className="space-y-4" data-id="element-431" data-mp="2xw4hjknjh4rg">
              {['One single point of contact per claim', 'One integrated invoice — no vendor reconciliation', 'One real-time dashboard for every stakeholder', 'One performance guarantee on outcomes and savings'].map(item => <li key={item} className="flex items-start" data-id="element-432" data-mp="31wwtwmrpjpry">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-433" data-mp="d5aubiuyje3g" />
                  <span className="text-gray-800 font-medium" data-id="element-434" data-mp="i3kuk7vjh4vx">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Service Catalog */}
      <section className="py-24 bg-brand-paper" data-id="element-435" data-mp="r5275g9qmofv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-436" data-mp="3qkyzdulld32e">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-437" data-mp="3qimdj6cpq8k2">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-438" data-mp="3un3teufyc1pk">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-439" data-mp="3e6fcj5a7t8m">
              Ancillary need, covered.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-440" data-mp="36xhveabbfsj5">
              From the first day of injury through return to work, we coordinate
              the full spectrum of ancillary services.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-441" data-mp="34az397qcovh3">
            {services.map((svc, i) => {
            const Icon = svc.icon;
            return <motion.div key={svc.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-442" data-mp="2udktts2uh6ya" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-443" data-mp="22meuveyv1fal">
                    <Icon className="w-6 h-6" data-id="element-444" data-mp="1i4fmyvwrd0jv" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-445" data-mp="1d7p7a9ov86yz">
                    {svc.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-446" data-mp="1rv1vk6624qt2">
                    {svc.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits / Stats */}
      <section className="py-24 bg-white" data-id="element-447" data-mp="u8uq6u1xiufn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-448" data-mp="ua5xv4yhwmc1">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-449" data-mp="1963o66lqmwgc">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-450" data-mp="146io1suu0i21">
              The A+ Difference
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-451" data-mp="2kixpx485m6mo">
              Measurable outcomes from day one.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-452" data-mp="2n4dx0fih9vgq">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-453" data-mp="2s1njpnehft0v" data-mpi={i}>
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-454" data-mp="312bzonnfs3ge"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-455" data-mp="3tkr0vr7w7cws" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-456" data-mp="3tn3q8pfzcpjv">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-457" data-mp="39gso95nmt29">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-458" data-mp="3ugb5135t7jyn">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-brand-paper" data-id="element-459" data-mp="3kiwxxu5s07j0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-460" data-mp="3frewz63r66y1">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-461" data-mp="3fq3x3hdfhjc7">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-462" data-mp="3bqv44eq7pphp">
              How It Works
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-463" data-mp="36om8falyg9bw">
              Simple referral. Seamless coordination.
            </h3>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-464" data-mp="2wtklmlf5jjch">
            <div className="hidden md:block absolute top-12 left-[12.5%] right-[12.5%] h-0.5 bg-gray-200" data-id="element-465" data-mp="2moumsof1ovch"></div>
            {[{
            step: '01',
            title: 'Submit referral',
            desc: 'One online form or fax sends your referral straight to our intake team.'
          }, {
            step: '02',
            title: 'We coordinate',
            desc: 'A dedicated coordinator schedules every service and verifies authorization.'
          }, {
            step: '03',
            title: 'Care delivered',
            desc: 'Injured workers receive timely, high-quality care from credentialed providers.'
          }, {
            step: '04',
            title: 'Transparent reporting',
            desc: 'Real-time updates and detailed outcomes reporting for every stakeholder.'
          }].map((s, i) => <motion.div key={s.step} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative text-center" data-id="element-466" data-mp="1q4lpzqz9382s" data-mpi={i}>
                <div className="w-24 h-24 mx-auto bg-white border-4 border-brand-gold rounded-full flex items-center justify-center mb-6 relative z-10 shadow-md" data-id="element-467" data-mp="afdyg0yz2hen">
                  <span className="font-serif font-bold text-2xl text-brand-navy" data-id="element-468" data-mp="oedo9by812ce">
                    {s.step}
                  </span>
                </div>
                <h4 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-469" data-mp="2q5qhv4uqnnt5">
                  {s.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-470" data-mp="2l902kjqrcw8s">
                  {s.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-471" data-mp="15qk7yo1kfjoz">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-472" data-mp="qz1yl937onel"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-473" data-mp="vxmn4b93pin7">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-474" data-mp="hyn9rzr1gq6c">
            See A+ Ancillary in action.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-475" data-mp="mzawwac5cmva">
            Schedule a 30-minute meeting with our team. We'll show you exactly
            how A+ Ancillary Services can drive savings and better outcomes for
            your program.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-476" data-mp="22g1oxz600s5z">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-477" data-mp="27cs3uj62hhpt">
              Schedule a Meeting
            </Link>
            <a href="tel:7142591053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-478" data-mp="20zxjslbixc3g">
              <Phone className="w-5 h-5 mr-2" data-id="element-479" data-mp="2olud4g5cs73a" /> 714.259.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}